import { childrenToString } from "../../internal/functions";
import { useTrackingId } from "../../hooks";
import { DataTrackingId, IanaZone } from "../../types";
import {
  MaskedDateRangeInput,
  MaskedDateRangeInputProps,
  MaskedDateRangeInputRef,
} from "./internal/MaskedDateRangeInput";
import { DateMode } from "../DateFieldSingle/types";
import { DateFieldRangeCalendar } from "./internal/DateFieldRangeCalendar";
import { Ref, useMemo, useRef } from "react";
import { useDateFieldRangeConversion } from "./internal/useDateFieldRangeConversion";
import {
  DateFieldRangeStateChange,
  useDateFieldRangeState,
} from "./internal/useDateFieldRangeState";
import { useFocusWithin } from "../../internal/hooks";
import { validateDateRange } from "./internal/utils";
import Popover from "../../internal/components/Popover";
import { mergeRefs } from "../../hooks/useMergeRefs/useMergeRefs";

export type DateFieldRangeChange = {
  /**
   * The start date in ISO 8601 format.
   * @example "2025-03-22"
   */
  startDate: string | null;
  /**
   * The end date in ISO 8601 format.
   * @example "2025-07-02"
   */
  endDate: string | null;
  /**
   * Whether the input field contains two parsable dates.
   * This is not the same as the date range being valid.
   *
   */
  isInputValid: boolean;
  /**
   * Whether the input field is empty.
   */
  isInputEmpty: boolean;
  /**
   * Whether the start and end dates are valid according to the constraints.
   * Constraints include:
   * - Required
   * - Unavailable dates
   * - Unavailable days of the week
   * - Min date
   * - Max date
   */
  isDateRangeValid: boolean;
};

export type DateFieldRangeChangeHandler = (
  change: DateFieldRangeChange,
) => void;

export type DateFieldRangeValue = {
  startDate: string | null;
  endDate: string | null;
} | null;

export type DateFieldRangeProps = Omit<
  MaskedDateRangeInputProps,
  | "onChange"
  | "value"
  | "autoComplete"
  | "onClick"
  | "onKeyDown"
  | "ref"
  | "required"
  | "startDate"
  | "endDate"
  | "defaultValue"
  | "onFocus"
  | "onBlur"
> & {
  value?: DateFieldRangeValue;
  defaultValue?: DateFieldRangeValue;
  onChange?: DateFieldRangeChangeHandler;
  mode?: Extract<DateMode, "mm/dd/yyyy" | "dd/mm/yyyy">;
  disableHint?: boolean;
  disableCalendar?: boolean;
  unavailable?: {
    dates?: string[];
    daysOfWeek?: (1 | 2 | 3 | 4 | 5 | 6 | 7)[];
  };
  minDate?: string;
  maxDate?: string;
  required?: boolean;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
  /**
   * The IANA timezone to use for the calendar. Controls the meaning of the "Today"
   * button and which month/year is initially displayed. When the timezone differs
   * from the user's locale timezone, a disambiguation message is shown below the calendar.
   * @example "America/Los_Angeles"
   */
  timezone?: IanaZone;
} & DataTrackingId;

export const DateFieldRange = (props: DateFieldRangeProps) => {
  const { aiMark, ...labelPropsWithoutAiMark } = props.labelProps || {};

  const data = {
    label: childrenToString(props.label),
    labelProps: labelPropsWithoutAiMark,
    description: childrenToString(props.description),
  };

  const trackingId = useTrackingId({
    name: "DateFieldRange",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  const {
    onFocus,
    onBlur,
    disableCalendar,
    required,
    mode,
    label,
    value: valueProp,
    defaultValue: defaultValueProp,
    minDate: minDateProp,
    maxDate: maxDateProp,
    unavailable: unavailableProp,
    onChange: onChangeProp,
    timezone,
    ...rest
  } = props;

  const popoverRef = useRef<{
    openPopover: () => void;
    closePopover: () => void;
  }>(null);

  const handleInputKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      popoverRef.current?.openPopover();
      requestAnimationFrame(() => {
        popoverContentRef.current?.querySelector("button")?.focus();
      });
    }
  };

  const { props: wrapperProps } = useFocusWithin({
    onBlur: (e) => {
      onBlur?.(e);
      popoverRef.current?.closePopover();
    },
    onFocus: onFocus,
  });

  const { value, defaultValue, minDate, maxDate, unavailable, onChange } =
    useDateFieldRangeConversion({
      value: valueProp,
      defaultValue: defaultValueProp,
      minDate: minDateProp,
      maxDate: maxDateProp,
      unavailable: unavailableProp,
      onChange: onChangeProp,
    });

  const inputRef = useRef<MaskedDateRangeInputRef>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const handleChange = (change: DateFieldRangeStateChange) => {
    const sharedConstraints = {
      unavailable,
      minDate: minDate ?? undefined,
      maxDate: maxDate ?? undefined,
    };

    onChange?.({
      startDate: change.startDate?.startOf("day") ?? null,
      endDate: change.endDate?.startOf("day") ?? null,
      isInputValid: change.isInputValid,
      isInputEmpty: change.isInputEmpty,
      isDateRangeValid: validateDateRange({
        required: required,
        startDate: change.startDate?.startOf("day") ?? null,
        endDate: change.endDate?.startOf("day") ?? null,
        startDateConstraints: sharedConstraints,
        endDateConstraints: sharedConstraints,
      }),
    });
  };

  const { startDate, endDate, handleInputChange, handleCalendarSelection } =
    useDateFieldRangeState({
      valueProp: value,
      defaultValueProp: defaultValue,
      onChange: handleChange,
    });

  const currentValidity = useMemo(() => {
    return validateDateRange({
      required,
      startDate,
      endDate,
      startDateConstraints: {
        unavailable,
        minDate: minDate ?? undefined,
        maxDate: maxDate ?? undefined,
      },
      endDateConstraints: {
        unavailable,
        minDate: minDate ?? undefined,
        maxDate: maxDate ?? undefined,
      },
    });
  }, [required, startDate, endDate, minDate, maxDate, unavailable]);

  const commonFieldProps = {
    ...rest,
    mode,
    label,
    onChange: handleInputChange,
    startDate: startDate ?? null,
    endDate: endDate ?? null,
    required,
    autoComplete: "off",
    "data-date-range-valid": currentValidity,
    "data-tracking-id": trackingId,
    "aria-label": label ? undefined : "Select a date range",
  };

  const justTheField = (
    <MaskedDateRangeInput {...commonFieldProps} ref={inputRef} />
  );

  if (disableCalendar) {
    return justTheField;
  }

  return (
    <div {...wrapperProps}>
      <Popover ref={popoverRef} placement="bottom-start" role="grid">
        <Popover.Trigger>
          {({ ref: iRef, onKeyDown }) => {
            const mergedRef = mergeRefs([
              inputRef,
              iRef as Ref<HTMLInputElement>,
            ]);
            return (
              <MaskedDateRangeInput
                {...commonFieldProps}
                ref={mergedRef}
                onKeyDown={(ev) => {
                  onKeyDown?.(ev);
                  handleInputKeyDown(ev);
                }}
                onClick={() => popoverRef.current?.openPopover()}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
          }}
        </Popover.Trigger>
        <Popover.Content ref={popoverContentRef} data-testid="calendar-popover">
          <DateFieldRangeCalendar
            startDate={startDate}
            endDate={endDate}
            onSelection={(v) => {
              if (v.endDate) {
                inputRef.current?.focus();
                popoverRef.current?.closePopover();
              }
              handleCalendarSelection(v);
            }}
            minDate={minDate}
            maxDate={maxDate}
            unavailable={unavailable}
            timezone={timezone}
          />
        </Popover.Content>
      </Popover>
    </div>
  );
};

DateFieldRange.displayName = "DateFieldRange";
